import { EventType, InteractionRequiredAuthError } from "@azure/msal-browser";

const tokenRefreshBufferSeconds = 300;

export async function initializeAuthentication(instance) {
  await instance.initialize();
  const accounts = instance.getAllAccounts();
  // One cached account is unambiguous. Multiple accounts require selection,
  // never an arbitrary first-entry or most-recent guess.
  if (!instance.getActiveAccount() && accounts.length === 1) {
    instance.setActiveAccount(accounts[0]);
  }
  instance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
      instance.setActiveAccount(event.payload.account);
    }
    // Silent token events never select an account: a late A result must
    // not replace an explicitly selected B account.
  });
}

export function isInteractionRequiredError(error) {
  const errors = [error, error?.cause, error?.networkError, ...(error?.graphQLErrors || [])].filter(Boolean);

  return errors.some(
    (currentError) =>
      currentError instanceof InteractionRequiredAuthError ||
      [
        "interaction_required",
        "login_required",
        "consent_required",
        "no_tokens_found",
        "monitor_window_timeout",
      ].includes(currentError?.errorCode),
  );
}

export function isAuthenticationResponseInIframe(browserWindow = window) {
  if (browserWindow.self === browserWindow.top) {
    return false;
  }

  const hashParams = new URLSearchParams(browserWindow.location.hash.slice(1));
  return hashParams.has("state") && (hashParams.has("code") || hashParams.has("error"));
}

export async function acquireAuthenticationToken(instance, account, scopes) {
  if (!account) {
    throw new Error("No active account is available");
  }

  let authResult = await instance.acquireTokenSilent({
    scopes,
    account,
  });
  const expiresAt = authResult.expiresOn?.getTime?.();

  if (!Number.isFinite(expiresAt)) {
    throw new Error("A current account-bound API access token is required");
  }
  if (expiresAt - Date.now() < tokenRefreshBufferSeconds * 1000) {
    authResult = await instance.acquireTokenSilent({
      scopes,
      account,
      forceRefresh: true,
    });
  }

  if (
    typeof authResult.accessToken !== "string" ||
    !authResult.accessToken ||
    authResult.accessToken.trim() !== authResult.accessToken ||
    !Number.isFinite(authResult.expiresOn?.getTime?.()) ||
    authResult.expiresOn.getTime() <= Date.now() ||
    accountSessionKey(authResult.account) !== accountSessionKey(account)
  ) {
    throw new Error("A current account-bound API access token is required");
  }
  return authResult.accessToken;
}

export function accountSessionKey(account) {
  // Stable across token renewals; different tenant/local accounts never share
  // an Apollo cache. This binding is not server authorization.
  return account
    ? JSON.stringify([account.homeAccountId, account.tenantId, account.localAccountId])
    : "signed-out";
}
